import * as React from 'react'
import type { TranscriptUtterance } from '@/client/types.gen'
import {
	formatTimestamp,
	renderHighlightedText,
} from '@/components/project/artifact-viewer/transcript/utils'
import { useDeferredInput } from '@/hooks/useDeferredInput'
import { cn } from '@/lib/utils'

export interface TranscriptUtteranceRowProps {
	utterance: TranscriptUtterance
	originalIdx: number
	editedText: string
	currentSpeaker: string
	speakerColor: string
	isActive: boolean
	onSeekTo?: (timeMs: number) => void
	onTextChange: (index: number, value: string, textarea: HTMLTextAreaElement | null) => void
	registerTextareaRef: (index: number, el: HTMLTextAreaElement | null) => void
	/** Find match ranges in this row (local offsets); when set, show highlighted text until user clicks to edit */
	localMatchRanges?: Array<{ start: number; end: number }>
	/** Index into localMatchRanges for the current match (purple highlight) */
	currentLocalMatchIndex?: number
}

export function TranscriptUtteranceRow({
	utterance,
	originalIdx,
	editedText,
	currentSpeaker,
	speakerColor,
	isActive,
	onSeekTo,
	onTextChange,
	registerTextareaRef,
	localMatchRanges = [],
	currentLocalMatchIndex = -1,
}: TranscriptUtteranceRowProps) {
	const formattedTime = formatTimestamp(utterance.start)
	const showHighlightView = localMatchRanges.length > 0
	const [isEditing, setIsEditing] = React.useState(false)
	const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

	const { localValue, handleChange, handleFocus, handleBlur } = useDeferredInput(
		editedText,
		(value) => onTextChange(originalIdx, value, textareaRef.current)
	)

	React.useEffect(() => {
		if (isEditing && textareaRef.current) {
			textareaRef.current.focus()
		}
	}, [isEditing])

	const handleRegisterRef = React.useCallback(
		(idx: number, el: HTMLTextAreaElement | null) => {
			textareaRef.current = el
			registerTextareaRef(idx, el)
		},
		[registerTextareaRef]
	)

	const resizeTextarea = React.useCallback(() => {
		const textarea = textareaRef.current
		if (!textarea) return
		textarea.style.height = 'auto'
		textarea.style.height = `${textarea.scrollHeight}px`
	}, [])

	// biome-ignore lint/correctness/useExhaustiveDependencies: localValue drives height when text updates
	React.useLayoutEffect(() => {
		requestAnimationFrame(resizeTextarea)
	}, [localValue, resizeTextarea])

	const displayHighlight = showHighlightView && !isEditing

	return (
		<div className="relative mb-3 pl-12">
			<button
				type="button"
				className={cn(
					'absolute left-0 top-1 font-mono text-xs cursor-pointer transition-colors',
					isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
				)}
				style={{ color: isActive ? speakerColor : undefined }}
				onClick={() => onSeekTo?.(utterance.start)}
				aria-label={`Seek to ${formattedTime}`}
			>
				{formattedTime}
			</button>

			<span className="font-semibold text-sm mr-1.5" style={{ color: speakerColor }}>
				{currentSpeaker}:
			</span>

			{displayHighlight ? (
				<button
					type="button"
					onClick={() => setIsEditing(true)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							setIsEditing(true)
						}
					}}
					className={cn(
						'w-full min-h-[1.5rem] text-base text-foreground leading-relaxed cursor-text border-0 bg-transparent p-0 text-left',
						isActive && 'bg-[#8c52ff]/10 px-1 -mx-1 rounded'
					)}
					aria-label={`${currentSpeaker} utterance, click to edit`}
				>
					{renderHighlightedText(editedText, localMatchRanges, currentLocalMatchIndex)}
				</button>
			) : (
				<textarea
					ref={(el) => handleRegisterRef(originalIdx, el)}
					value={localValue}
					onChange={(e) => {
						handleChange(e.target.value)
						resizeTextarea()
					}}
					onFocus={handleFocus}
					onBlur={() => {
						handleBlur()
						setIsEditing(false)
					}}
					className={cn(
						'w-full bg-transparent text-base text-foreground',
						'border-0 p-0 focus:outline-none focus:ring-0',
						'leading-relaxed overflow-hidden',
						isActive && 'bg-[#8c52ff]/10 px-1 -mx-1 rounded'
					)}
					style={{ resize: 'none' }}
					rows={1}
					aria-label={`${currentSpeaker} utterance`}
				/>
			)}
		</div>
	)
}
